import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { throwError as observableThrowError } from 'rxjs';
import { Endpoints } from './endpoints';

import { CdxApiGatewayHttpService } from '../../shared/services/cdx-api-gateway-http.service';
import type { CalculateFeeMegaRequest } from './interfaces/calculate-fee-mega-request';
import type { CommitMegaRequest } from './interfaces/commit-mega-request';
import type { DownloadMegaPdf } from './interfaces/download-mega-pdf';
import type { ListPrefixMegaRequest } from './interfaces/list-prefix-mega-request';
import type { RetrieveMegaRequest } from './interfaces/retrieve-mega-request';
import type { SettleDateInfoMegaRequest } from './interfaces/settle-date-info-mega-request';
import type { SettledMegaRequest } from './interfaces/settled-mega-request';
import type { VolumeSummaryRequest } from './interfaces/volume-summary-request';

@Injectable()
export class MegasService {
  private http = inject(CdxApiGatewayHttpService);
  private httpFileUpload = inject(HttpClient);

  public listPrefix(listPrefixRequest: ListPrefixMegaRequest | any): Observable<object> {
    return this.http.post(Endpoints.LIST_PREFIX_ENDPOINT, JSON.stringify(listPrefixRequest));
  }

  public settlementDate(
    settlementDateRequest: SettleDateInfoMegaRequest | any,
  ): Observable<object> {
    return this.http.post(
      Endpoints.SETTLE_DATE_INFO_ENDPOINT,
      JSON.stringify(settlementDateRequest),
    );
  }

  public commit(commitRequest: CommitMegaRequest | any): Observable<object> {
    return this.http.post(Endpoints.MEGA_COMMIT_ENDPOINT, JSON.stringify(commitRequest));
  }

  public calculateFee(calculateFeeRequest: CalculateFeeMegaRequest | any): Observable<object> {
    return this.http.post(
      Endpoints.MEGA_CALCULATE_FEE_ENDPOINT,
      JSON.stringify(calculateFeeRequest),
    );
  }

  public collateralUploadCalculateFee(calcFeeRequest: any): Observable<object> {
    return this.http.post(
      Endpoints.CU_MULTI_MEGA_CALCULATE_FEE_ENDPOINT,
      JSON.stringify(calcFeeRequest),
    );
  }

  public listCommittedMega(): Observable<object> {
    return this.http.post(Endpoints.MEGA_LIST_COMMITTED, JSON.stringify({}));
  }

  public listSettledMega(settledMegaReq: SettledMegaRequest): Observable<object> {
    return this.http.post(Endpoints.MEGA_LIST_SETTLED, JSON.stringify(settledMegaReq));
  }

  public retrieveMega(retrieveMegaReq: RetrieveMegaRequest): Observable<object> {
    return this.http.post(Endpoints.MEGA_RETRIEVE_ENDPOINT, JSON.stringify(retrieveMegaReq));
  }

  public downloadMegaPDF(downloadMegaPDFRequest: DownloadMegaPdf): Observable<object> {
    return this.http.post(
      Endpoints.MEGA_DOWNLOAD_PDF_ENDPOINT,
      JSON.stringify(downloadMegaPDFRequest),
      null,
      'text',
    );
  }

  public uploadCollateral(formData: any, options: any, url: string): Observable<object> {
    return this.httpFileUpload.put(url, formData, options);
  }

  public uploadCollateralEndpoint(request: any): Observable<object> {
    return this.http.post(
      Endpoints.MEGA_COLLATERAL_UPLOAD_SIGNED_URL_ENDPOINT,
      JSON.stringify(request),
    );
  }

  private handleError(error: any) {
    return observableThrowError(() => error);
  }

  public monthlyVolumeSummary(
    volumeSummaryRequest: VolumeSummaryRequest | any,
  ): Observable<object> {
    return this.http.post(
      Endpoints.MEGA_MONTHLY_VOLUME_SUMMARY,
      JSON.stringify(volumeSummaryRequest),
    );
  }

  public statusSummaryCount(request: any): Observable<object> {
    return this.http.post(
      Endpoints.MEGA_DASHBOARD_STATUS_SUMMARY_FOR_COUNT,
      JSON.stringify(request),
    );
  }

  public upcomingMegaSettlements(request: any): Observable<object> {
    return this.http.post(
      Endpoints.MEGA_DASHBOARD_UPCOMING_MEGA_SETTLEMENTS,
      JSON.stringify(request),
    );
  }

  public megasPoolCollateralDue(): Observable<object> {
    return this.http.post(Endpoints.MEGA_POOL_COLLATERAL_DUE, JSON.stringify({}));
  }

  public uploadLocation(request: any): Observable<object> {
    return this.http.post(
      Endpoints.UPLOAD_POOL_INFO_UPLOAD_LOCATION_ENDPOINT,
      JSON.stringify(request),
    );
  }

  public cancelUpload(request: any): Observable<object> {
    return this.http.post(
      Endpoints.UPLOAD_POOL_INFO_CANCEL_UPLOAD_ENDPOINT,
      JSON.stringify(request),
    );
  }

  public downloadXLSXPoolInfoTemplate(): Observable<object> {
    return this.http.post(
      Endpoints.UPLOAD_POOL_INFO_DOWNLOAD_XLSX_TEMPLATE,
      JSON.stringify({}),
      null,
      'text',
    );
  }

  public uploadStatus(request: any): Observable<object> {
    return this.http.post(
      Endpoints.UPLOAD_POOL_INFO_UPLOAD_STATUS_ENDPOINT,
      JSON.stringify(request),
    );
  }

  public validationStatus(request: any): Observable<object> {
    return this.http.post(
      Endpoints.UPLOAD_POOL_INFO_VALIDATION_STATUS_ENDPOINT,
      JSON.stringify(request),
    );
  }

  public validatedMegaList(request: any): Observable<object> {
    return this.http.post(Endpoints.ALLOCATE_POOL_VALIDATED_MEGA_ENDPOINT, JSON.stringify(request));
  }

  public deallocateCollateral(request: any): Observable<object> {
    return this.http.post(Endpoints.DEALLOCATE_COLLATERAL_ENDPOINT, JSON.stringify(request));
  }

  public deallocateFreddieCollateral(request: any): Observable<object> {
    return this.http.post(
      Endpoints.DEALLOCATE_FREDDIE_COLLATERAL_ENDPOINT,
      JSON.stringify(request),
    );
  }

  public uploadCollateralCommit(request: any): Observable<object> {
    return this.http.post(Endpoints.UPLOAD_POOL_INFO_COMMIT_ENDPOINT, JSON.stringify(request));
  }

  public cuMultiMegaCommit(request: any | any): Observable<object> {
    return this.http.post(Endpoints.CU_MULTI_COMMIT_ENDPOINT, JSON.stringify(request));
  }

  public confirmationStatus(request: any): Observable<object> {
    return this.http.post(
      Endpoints.UPLOAD_POOL_INFO_COMMIT_ALLOCATION_STATUS_ENDPOINT,
      JSON.stringify(request),
    );
  }

  public downloadErrorReport(request: any, observe: any): Observable<object> {
    return this.http.post(
      Endpoints.UPLOAD_POOL_INFO_DOWNLOAD_ERROR_REPORT_ENDPOINT,
      JSON.stringify(request),
      observe,
      'text',
    );
  }

  public multiMegaValidate(
    validateMultiMegaRequest: CalculateFeeMegaRequest | any,
  ): Observable<object> {
    return this.http.post(
      Endpoints.MULTI_MEGA_VALIDATE_ENDPOINT,
      JSON.stringify(validateMultiMegaRequest),
    );
  }

  public multiMegaCalculateFee(
    calculateFeeRequest: CalculateFeeMegaRequest | any,
  ): Observable<object> {
    return this.http.post(
      Endpoints.MULTI_MEGA_CALCULATE_FEE_ENDPOINT,
      JSON.stringify(calculateFeeRequest),
    );
  }

  public multipleMegaCommit(multiMegaCommitReq: any | any): Observable<object> {
    return this.http.post(Endpoints.MULTI_MEGA_COMMIT_ENDPOINT, JSON.stringify(multiMegaCommitReq));
  }

  public exportMultiMegaPDFConfirmationDetails(request: any, options: any): Observable<object> {
    return this.http.post(
      Endpoints.MULTI_MEGA_EXPORT_PDF_CONFIRMATION_DETAIL_ENDPOINT,
      JSON.stringify(request),
      options,
      'text',
    );
  }

  public exportMultiMegaExcelConfirmationDetails(request: any, options: any): Observable<object> {
    return this.http.post(
      Endpoints.MULTI_MEGA_EXPORT_EXCEL_CONFIRMATION_DETAIL_ENDPOINT,
      JSON.stringify(request),
      options,
      'text',
    );
  }

  public collateralAnalyzerValidateCollateral(request: any): Observable<object> {
    return this.http.post(
      Endpoints.COLLATERAL_ANALYZER_VALIDATE_COLLATERAL_ENDPOINT,
      JSON.stringify(request),
    );
  }

  public checkFactorData(): Observable<object> {
    return this.http.post(
      Endpoints.COLLATERAL_ANALYZER_CHECK_FACTOR_DATA_ENDPOINT,
      JSON.stringify({}),
    );
  }

  public collateralAnalyzerAllocateCollateral(request: any): Observable<object> {
    return this.http.post(Endpoints.ALLOCATE_COLLATERAL_ENDPOINT, JSON.stringify(request));
  }

  public downloadCollateralAnalyzerExcelConfirmationDetails(
    request: any,
    options: any,
  ): Observable<object> {
    return this.http.post(
      Endpoints.COLLATERAL_ANALYZER_DOWNLOAD_EXCEL_ENDPOINT,
      JSON.stringify(request),
      options,
      'text',
    );
  }

  public downloadAnalyzerPDF(downloadMegaPDFRequest: DownloadMegaPdf): Observable<object> {
    return this.http.post(
      Endpoints.COLLATERAL_ANALYZER_DOWNLOAD_PDF_ENDPOINT,
      JSON.stringify(downloadMegaPDFRequest),
      null,
      'text',
    );
  }

  public getDisclosureTempMega(request: any): Observable<object> {
    return this.http.post(
      Endpoints.COLLATERAL_ANALYZER_DISCLOSURE_TEMPMEGA_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
      'text',
    );
  }

  public getONPREMDisclosureTempMega(request: any): Observable<object> {
    return this.http.post(
      Endpoints.ONPREM_COLLATERAL_ANALYZER_DISCLOSURE_TEMPMEGA_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
      'text',
    );
  }

  public getDisclosureWADetails(request: any): Observable<object> {
    return this.http.post(
      Endpoints.COLLATERAL_ANALYZER_DISCLOSURE_WADETAILS_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
    );
  }

  public getONPREMDisclosureWADetails(request: any): Observable<object> {
    return this.http.post(
      Endpoints.ONPREM_COLLATERAL_ANALYZER_DISCLOSURE_WADETAILS_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
    );
  }

  public getStatisticsDetails(request: any): Observable<object> {
    return this.http.post(
      Endpoints.COLLATERAL_ANALYZER_DISCLOSURE_STATISTICS_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
    );
  }

  public getONPREMStatisticsDetails(request: any): Observable<object> {
    return this.http.post(
      Endpoints.ONPREM_COLLATERAL_ANALYZER_DISCLOSURE_STATISTICS_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
    );
  }

  public deleteTempMega(request: any): Observable<object> {
    return this.http.post(
      Endpoints.COLLATERAL_ANALYZER_DISCLOSURE_DELETE_TEMPMEGA_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
    );
  }

  public deleteONPREMTempMega(request: any): Observable<object> {
    return this.http.post(
      Endpoints.ONPREM_COLLATERAL_ANALYZER_DISCLOSURE_DELETE_TEMPMEGA_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
    );
  }

  public getQuartileStatisticsDetails(request: any): Observable<object> {
    return this.http.post(
      Endpoints.COLLATERAL_ANALYZER_DISCLOSURE_QUARTILE_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
    );
  }

  public getONPREMQuartileStatisticsDetails(request: any): Observable<object> {
    return this.http.post(
      Endpoints.ONPREM_COLLATERAL_ANALYZER_DISCLOSURE_QUARTILE_ENDPOINT,
      JSON.stringify(request),
      { overrideErrorHandler: true },
    );
  }

  public uploadCollateralDetails(request: any): Observable<object> {
    return this.http.post(
      Endpoints.UPLOAD_POOL_INFO_UPLOAD_COLLATERAL_ANALYZER_ENDPOINT,
      JSON.stringify(request),
    );
  }

  public downloadCollateralPool(request: any): Observable<object> {
    return this.http.post(Endpoints.DOWNLOAD_COLLATERAL_POOL_ENDPOINT, request);
  }
}
